import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { ShiftOptions } from 'models/ICattlesData'
import MilkingProcessService from 'services/milkingProcessService'

const ALL_SHIFT_PERIODS: ShiftOptions[] = [
	ShiftOptions.AM,
	ShiftOptions.PM,
	ShiftOptions.NIGHT,
]

const getAvailablePeriodsForParlors = (
	milkingShifts: Awaited<
		ReturnType<typeof MilkingProcessService.getMilkingShifts>
	>,
	parlorIds: number[],
) => {
	const periods = new Set<ShiftOptions>()

	parlorIds.forEach(id => {
		milkingShifts
			.filter(shift => shift.milking_parlor === id)
			.forEach(shift => periods.add(shift.period))
	})

	return periods
}

export const useUnavailableShiftsForParlors = (parlorId: number[] | 'all') => {
	const { data: milkingShifts = [] } = useQuery({
		queryKey: ['milking-shifts'],
		queryFn: () => MilkingProcessService.getMilkingShifts(),
		staleTime: 50000,
	})

	return useMemo(() => {
		if (parlorId === 'all' || parlorId.length === 0) {
			return [] as ShiftOptions[]
		}

		const availablePeriods = getAvailablePeriodsForParlors(
			milkingShifts,
			parlorId,
		)

		return ALL_SHIFT_PERIODS.filter(period => !availablePeriods.has(period))
	}, [milkingShifts, parlorId])
}

export const useParlorAvailableShiftPeriods = (parlorId: number | null) => {
	const {
		data: milkingShifts = [],
		isLoading,
		isFetched,
	} = useQuery({
		queryKey: ['milking-shifts'],
		queryFn: () => MilkingProcessService.getMilkingShifts(),
		staleTime: 50000,
	})

	const availablePeriods = useMemo(() => {
		if (!parlorId) {
			return new Set<ShiftOptions>()
		}

		return getAvailablePeriodsForParlors(milkingShifts, [parlorId])
	}, [milkingShifts, parlorId])

	const isShiftsLoading =
		Boolean(parlorId) &&
		(isLoading || !isFetched) &&
		availablePeriods.size === 0

	return { availablePeriods, isLoading: isShiftsLoading }
}
